import FreestarAdSlot from '@freestar/pubfig-adslot-react-component';
import type { AdSlotExtended } from 'interfaces/ads/AdSlot';
import { useContext, useMemo } from 'react';
import { AdContext } from 'services/Ad';
import { checkForDirectSoldAd } from 'services/Ad/utils/directSoldAdCheck';
import { freeStarPlacementNameFinder, publisher } from 'services/Ad/utils/freeStarConfig';
import { applyInitialStickyBehavior, leaderboardManager } from 'services/Ad/utils/leaderboardManager';
import type { PlacementName } from 'interfaces/ads/Ad';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

const AdSlot: React.FC<AdSlotExtended> = ({ placementName, index = 0, sticky = false }: AdSlotExtended) => {
  const adContext = useContext(AdContext);
  const { getPlacementConfig: getAdSlotConfig, tags, ctype } = adContext;
  const { userFlags: userVariants } = useOptimizely();
  const variants = userVariants?.crownInitalHeight as { enabled: boolean };
  const placementConfig = getAdSlotConfig(placementName);
  const finalPath: PlacementName = freeStarPlacementNameFinder(
    placementName,
    sticky,
    index,
    placementConfig.path,
  ) as PlacementName;
  const classToUse = placementName === 'leaderboard_top' ? 'leaderboard-ad-wrapper' : 'ad-slot-wrapper';

  const freeStarSlot = useMemo(() => {
    const slot: AdSlotExtended = {
      publisher,
      placementName: finalPath,
      classList: [classToUse],
      targeting: { placement_name: placementName, index, sticky },
      channel: placementConfig.path,
    };
    if (placementName === 'Leaderboard') {
      slot.slotId = `Leaderboard0`;
      slot.onNewAdSlotsHook = (e) => {
        if (e === 'fortunecom_leaderboard_top') {
          leaderboardManager();
          if (variants?.enabled) applyInitialStickyBehavior();
        }
      };
    }
    const directSoldAd = checkForDirectSoldAd(tags, ctype, placementName, placementConfig);
    if (directSoldAd.sizeMappings) {
      slot.adUnitPath = placementConfig.path;
      slot.slotSize = directSoldAd.slotSize;
      slot.sizeMappings = directSoldAd.sizeMappings;
    }
    return slot;
  }, [placementName, index, sticky, placementConfig.path, tags, ctype, variants?.enabled, finalPath, classToUse]);

  if (!placementConfig.path) return null;

  return (
    <FreestarAdSlot
      key={`${placementName}-${index}-${sticky}-${placementConfig.path}`}
      {...freeStarSlot}
    />
  );
};

export default AdSlot;
